import React, {useCallback, useMemo, useRef, useState} from 'react';
import { connect } from 'react-redux';
import {IconButton, Tooltip, Typography} from '@material-ui/core';
import WorkDiagnoseReportList from "./WorkDiagnoseReportList";
import {CloudDownload, Replay} from "@material-ui/icons";
import {hasPermission, formatDate, getErasureReportStatus} from '../../sc-utilities';
import { snackActions } from '../../sc-utilities/SnackBar';
import config from "../../../config/config";
import {download} from "../../sc-utilities/download.utils";
import { CustomChip } from '../../sc-productComponents/Report/ErasureReport';
import ReportRegenerate from '../../sc-productComponents/Report/ErasureReport/ReportRegenerate';
import * as workDiagnoseReportActions from "./store/workDiagnoseReportActions";

let downloadObj = {}

const WorkDiagnoseReport = props => {
    const tableRef = useRef();
    const { columns, translations, permissions, activeCustomerID, onRegenerateReport, productID, onGenerate } = props;

    const visible = columns && columns['WorkDiagnoseReport'];

    const [regenerateAction, setRegenerateAction] = useState(0);
    const [regenerateModalState, setRegenerateModalState] = useState({ open: false, diagnoseReportId: null });

    const handleCloseSafe = () => {
        setRegenerateModalState({ open: false, diagnoseReportId: null });
    };

    const handleCloseDanger = useCallback(async (diagnoseReportId = null) => {
        let result = await onRegenerateReport({ ReportID: diagnoseReportId ? diagnoseReportId : regenerateModalState.diagnoseReportId, ActiveCustomerID: activeCustomerID, ErasureReportType: "diagnose-pdf" });
        setRegenerateModalState({ open: false, diagnoseReportId: null });

        if (result.data) {
            setRegenerateAction(regenerateAction + 1);
        }
        tableRef.current && tableRef.current.onQueryChange();
    }, [regenerateModalState, regenerateAction, onRegenerateReport, activeCustomerID]);

    const handleRegenerate = useCallback((diagnoseReportId, generatedStatus) => {
        if (generatedStatus === -1) {
            handleCloseDanger(diagnoseReportId);
        } else {
            setRegenerateModalState({ ...regenerateModalState, open: true, diagnoseReportId: diagnoseReportId });
        }
    }, [regenerateModalState, handleCloseDanger]);

    let tableColumns = useMemo(() => {

        const downloadReport = async diagnoseReportId => {
            const objectURL = `${config.backend_domain}:${config.backend_port}/report/download?ReportID=${diagnoseReportId}&ErasureReportType=diagnose-pdf&ActiveCustomerID=${activeCustomerID}`;
            let exportDataSuccessful = await download(objectURL);
            tableRef.current && tableRef.current.onQueryChange();
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        }

        const createAction = (reportId, originalFileName, generatedStatus = null, generated, translations) => {
            return <div>
                {hasPermission(permissions, 'erasure_report_download') && generatedStatus === 2 ?
                    <Tooltip title={translations["download_report"] || "Download report(s)"}>
                        <IconButton component='div' disabled={downloadObj ? downloadObj[reportId] : false} aria-label={translations["download_report"] || "Download report(s)"}
                                    onClick={() => {
                                        if (downloadObj ? downloadObj[reportId] : false) return null; else return downloadReport(reportId, originalFileName)
                                    }}>
                            {<CloudDownload />}
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {hasPermission(permissions, 'erasure_report_regenerate') && (generatedStatus === 1 || generatedStatus === -1) ?
                    <Tooltip title={translations["regenerate_diagnose_report"] || "Regenerate diagnose report"}>
                        <IconButton onClick={() => handleRegenerate(reportId, generatedStatus)} component='div' aria-label={translations["regenerate_diagnose_report"] || "Regenerate diagnose report"}>
                            <Replay />
                        </IconButton>
                    </Tooltip>
                    : null
                }
            </div>
        };
        return [
            {
                title: translations["actions"] || 'Action(s)', field: 'PdfReportID',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 200, maxWidth: 200 },
                filtering: false,
                render: rowData => {
                    return <>
                        {createAction(rowData.ErasureReportID, rowData.OriginalFileName, rowData.GeneratedStatus, rowData.Generated, translations)}

                    </>
                }
            },
            {
                title: translations["requested_by"] || 'Requested By', field: 'Username',
                hidden: visible ? visible['Username'] : false,
                cellStyle: { minWidth: 220, maxWidth: 220 },
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.User && rowData.User.Username ? rowData.User.Username : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["requested_at"] || 'Requested at', field: 'RequestedAt',
                hidden: visible ? visible['RequestedAt'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.RequestedAt ? formatDate(rowData.RequestedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["number_of_downloads"] || 'Number of downloads', field: 'DownloadedNumber',
                hidden: visible ? visible['DownloadedNumber'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.DownloadedNumber ? rowData.DownloadedNumber : '0'}</Typography>;
                }
            },
            {
                title: translations["original_file_name"] || 'Original file name', field: 'OriginalFileName',
                hidden: visible ? visible['OriginalFileName'] : false,
                filtering: true,
                selection: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.OriginalFileName ? rowData.OriginalFileName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'GeneratedStatus',
                hidden: visible ? visible['GeneratedStatus'] : false,
                lookup: { '0': translations['ready_to_generate'] || 'Ready to generate', '1': translations['in_progress'] || "In progress", '2': translations['generated'] || 'Generated', '-1': translations['failed'] || 'Failed' },
                defaultFilter: ['0', '1', '2', '-1'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <CustomChip trimming={true} customChipKey={rowData.ColliInventoryID}
                                       className={`chip${getErasureReportStatus(rowData.GeneratedStatus, 'forStyle')}`}
                                       value={getErasureReportStatus(rowData.GeneratedStatus, null, null, translations)} />
                }
            },
        ]
    }, [translations, visible, activeCustomerID, permissions, handleRegenerate]);

    return (
        <>
            {hasPermission(permissions, 'diagnose_report_list') &&
                <WorkDiagnoseReportList
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                    productID={productID}
                    onGenerate={onGenerate}
            />}
            {hasPermission(permissions, 'erasure_report_regenerate') &&
                <ReportRegenerate
                    entityName={translations['diagnose_report_tab'] || 'Diagnose report'}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    regenerateModalState={regenerateModalState}
                    diagnose={true}
                />
            }
        </>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};
const mapDispatchToProps = dispatch => {
    return {
        onRegenerateReport: data => dispatch(workDiagnoseReportActions.regenerateReport(data)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(WorkDiagnoseReport);
